/**
 * Image URL utilities for optimized image loading
 * Uses Supabase Image Transformations for on-the-fly resizing
 *
 * Transform URL pattern:
 * Original: https://xxx.supabase.co/storage/v1/object/public/bucket/path
 * Transformed: https://xxx.supabase.co/storage/v1/render/image/public/bucket/path?width=400&quality=75
 *
 * Note: format=webp is NOT supported on this Supabase project - do not add it
 */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || 'https://zxsnritknqktyixcapmt.supabase.co';

interface TransformOptions {
  width?: number;
  height?: number;
  quality?: number;
  resize?: 'cover' | 'contain' | 'fill';
}

/**
 * Supabase image transformations accept dimensions of 1–2500px. A request
 * outside that range is REJECTED, and the failure is worse than it sounds on an
 * `<img srcset>`: when the candidate the browser picked fails to load, it does
 * not fall back to a smaller candidate — it fires `error` and shows nothing. So
 * one oversized entry in a srcset silently blanks the image on exactly the
 * high-DPI screens that selected it.
 *
 * Clamping here rather than at each call site means a caller cannot introduce
 * that failure by asking for a number that looks reasonable. Found by Codex on
 * the HOME-03 hero, whose 2× candidate was 2624.
 */
const SUPABASE_MAX_TRANSFORM_DIMENSION = 2500;

const clampDimension = (value: number): number =>
  Math.max(1, Math.min(Math.round(value), SUPABASE_MAX_TRANSFORM_DIMENSION));

/**
 * Append transform parameters to a base URL
 * Note: format parameter is intentionally NOT included - not supported on this project
 */
function appendTransformParams(baseUrl: string, options: TransformOptions): string {
  const params = new URLSearchParams();
  if (options.width) params.set('width', clampDimension(options.width).toString());
  if (options.height) params.set('height', clampDimension(options.height).toString());
  if (options.quality) params.set('quality', options.quality.toString());
  // ALWAYS include resize=contain to maintain aspect ratio (without it, Supabase
  // only resizes width but keeps original height, creating distorted images)
  params.set('resize', options.resize || 'contain');

  const paramString = params.toString();
  return paramString ? `${baseUrl}?${paramString}` : baseUrl;
}

/**
 * Convert any Supabase storage URL to an optimized transform URL.
 * Handles both full URLs and relative paths.
 *
 * @param url - Full Supabase URL or relative storage path
 * @param options - Transform options
 * @returns Optimized image URL, or original URL if not a Supabase image
 */
export function getOptimizedUrl(
  url: string | null | undefined,
  options: TransformOptions = {}
): string {
  if (!url) return '/placeholder-property.svg';

  // Only transform Supabase storage URLs
  if (!url.includes(SUPABASE_URL) && !url.includes('supabase.co/storage')) {
    // Handle Unsplash URLs with their own optimization
    if (url.includes('unsplash.com')) {
      try {
        const unsplashUrl = new URL(url);
        if (options.width) unsplashUrl.searchParams.set('w', options.width.toString());
        unsplashUrl.searchParams.set('q', (options.quality || 80).toString());
        unsplashUrl.searchParams.set('fm', 'webp');
        unsplashUrl.searchParams.set('fit', 'crop');
        return unsplashUrl.toString();
      } catch {
        return url;
      }
    }
    return url; // External URL — return as-is
  }

  // Already a transform URL? Update params
  if (url.includes('/render/image/public/')) {
    const baseUrl = url.split('?')[0];
    return appendTransformParams(baseUrl, options);
  }

  // Convert /object/public/ → /render/image/public/
  if (url.includes('/object/public/')) {
    const transformUrl = url.replace('/object/public/', '/render/image/public/');
    return appendTransformParams(transformUrl, options);
  }

  // Relative path — construct full transform URL
  // Detect bucket from path or default to listing-images
  const bucket = url.startsWith('avatars/') ? 'avatars'
    : url.startsWith('agency-logos/') ? 'agency-logos'
    : 'listing-images';

  const transformUrl = `${SUPABASE_URL}/storage/v1/render/image/public/${bucket}/${url}`;
  return appendTransformParams(transformUrl, options);
}

// ============================================
// Convenience functions for specific contexts
// ============================================

/**
 * Property card thumbnail (Home page grid) - 400px width, q75
 * Expected size: ~50-80 KB
 */
export function getCardThumbnailUrl(url: string | null | undefined): string {
  return getOptimizedUrl(url, { width: 400, quality: 75 });
}

/**
 * Property details hero image - 800px width, q80
 * Expected size: ~100-200 KB
 */
export function getHeroImageUrl(url: string | null | undefined): string {
  return getOptimizedUrl(url, { width: 800, quality: 80 });
}

/**
 * Gallery/lightbox full image - 1200px width, q85
 * Expected size: ~200-350 KB
 */
export function getGalleryImageUrl(url: string | null | undefined): string {
  return getOptimizedUrl(url, { width: 1200, quality: 85 });
}

/**
 * Agency logo (small) - 80px width, q75
 * Expected size: ~10-20 KB
 */
export function getAgencyLogoUrl(url: string | null | undefined): string {
  return getOptimizedUrl(url, { width: 80, quality: 75 });
}

/**
 * Get optimized avatar URL with automatic resizing
 * @param path - The avatar path in Supabase storage or full URL
 * @param size - The desired size in pixels (default: 64)
 * @returns Optimized avatar URL
 */
export function getAvatarUrl(path: string | null | undefined, size: number = 64): string {
  if (!path) {
    return `https://ui-avatars.com/api/?name=Agent&size=${size}&background=134025&color=fff`;
  }

  return getOptimizedUrl(path, {
    width: size,
    height: size,
    quality: 75,
    resize: 'cover'
  });
}

/**
 * Responsive srcset for property images
 * Generates 400w, 800w, 1200w variants
 */
export function getPropertySrcSet(url: string | null | undefined): string {
  if (!url) return '';
  return [400, 800, 1200]
    .map(w => `${getOptimizedUrl(url, { width: w, quality: 80 })} ${w}w`)
    .join(', ');
}

/**
 * Guide / blog hero image (LCP element).
 *
 * Full-bleed hero served from the Supabase `blog-images` bucket. Rewrites the
 * stored `/object/public/` URL to the `/render/image/public/` endpoint at
 * quality 70. WebP is negotiated automatically by the render endpoint when the
 * browser sends `Accept: image/webp` (no `format` param needed), taking a
 * 3.5 MB raw JPEG down to ~110 KB.
 *
 * Widths match the hero breakpoints (mobile → desktop) used with sizes="100vw".
 */
export const GUIDE_HERO_WIDTHS = [640, 828, 1200] as const;
const GUIDE_HERO_QUALITY = 70;

/**
 * Fallback `src` / preload `href` for the guide hero — the largest variant.
 * Use this for BOTH the <img src> and the <link rel="preload" href> so the
 * non-srcset fallback resolves to the same candidate.
 */
export function getGuideHeroSrc(url: string | null | undefined): string {
  return getOptimizedUrl(url, { width: 1200, quality: GUIDE_HERO_QUALITY });
}

/**
 * Responsive srcset for the guide hero (640w/828w/1200w @ q70).
 * The exact same string must be used for the <img srcset> and the
 * <link rel="preload" imagesrcset> — otherwise the browser downloads the
 * hero twice (once for the preload, once for the img).
 */
export function getGuideHeroSrcSet(url: string | null | undefined): string {
  if (!url) return '';
  return GUIDE_HERO_WIDTHS
    .map(w => `${getOptimizedUrl(url, { width: w, quality: GUIDE_HERO_QUALITY })} ${w}w`)
    .join(', ');
}

/**
 * HOME-03 — the Home hero banner (the page's LCP element).
 *
 * The banner is contained in the 1440 content shell with a 64px gutter, so it
 * is at most 1312 CSS px wide. 2624 covers a 2× display and there is no reason
 * to fetch anything larger — this is the first paint on the highest-traffic
 * page in the product, and it loads eager at high priority.
 *
 * Before this the hero rendered the RAW url with no transform and no srcset, so
 * whatever was uploaded is what every visitor downloaded at full resolution.
 * Quality 72 rather than 80: this is a background behind two opaque cards, and
 * the render endpoint negotiates WebP automatically when the browser sends
 * `Accept: image/webp`.
 */
/**
 * The top candidate is 2500, not the 2624 a 2× render of the 1312px slot would
 * ask for: 2500 is the ceiling Supabase's transform accepts, and a rejected
 * candidate is not retried at a smaller size — the browser gives up and the
 * hero falls back to its gradient on precisely the high-DPI desktops that chose
 * it. 2500 against a 1312 slot is 1.9×, which is indistinguishable from 2×, and
 * the source is exported at 2500 too (config/homeImages.ts), so the top
 * candidate is a no-op resize rather than an upscale.
 */
export const HERO_BANNER_WIDTHS = [828, 1312, 1968, 2500] as const;
const HERO_BANNER_QUALITY = 72;

/** Fallback `src` — the largest candidate, for browsers ignoring srcset. */
export function getHeroBannerSrc(url: string | null | undefined): string {
  return getOptimizedUrl(url, { width: 2500, quality: HERO_BANNER_QUALITY });
}

export function getHeroBannerSrcSet(url: string | null | undefined): string {
  if (!url) return '';
  return HERO_BANNER_WIDTHS
    .map(w => `${getOptimizedUrl(url, { width: w, quality: HERO_BANNER_QUALITY })} ${w}w`)
    .join(', ');
}

/**
 * HOME-03 — a "Browse by area" tile.
 *
 * The tile renders at 250×120 on desktop and ~166–191×110 on mobile, so 500
 * covers a 2× desktop tile with room. `getCardThumbnailUrl`'s 400 was built for
 * listing cards and is the wrong number here by coincidence rather than by
 * design; giving the tiles their own function means changing one without
 * touching the other.
 */
export function getAreaTileUrl(url: string | null | undefined): string {
  return getOptimizedUrl(url, { width: 500, quality: 75 });
}

/**
 * The tile's responsive candidates.
 *
 * The `<img>` carried a `sizes` attribute and no `srcset`, which makes `sizes`
 * inert — every tile fetched the single 500w file regardless of the box it was
 * about to fill.
 *
 * THE TWO-COLUMN GRID IS WIDEST JUST BELOW ITS BREAKPOINT, not at the tablet
 * width it is named for. It is `lg:hidden`, so it stays active all the way to
 * 1023, and the tile is `(100vw − 36px gutters − 12px gap) / 2`: 156 CSS px on
 * a 360 phone, 360 at 768, and **487.5 at 1023** — where a 2× screen needs 975
 * device pixels. The first version of this list topped out at 800 because it
 * was derived from a measurement taken at 768 and nowhere between there and
 * the breakpoint, so wide tablets upscaled by 1.22× and the photographs went
 * soft on exactly the screens with the pixels to show them. Found by Codex.
 *
 * 400 / 600 / 800 / 1000 covers 1× everywhere, 2× at every width the grid
 * reaches (worst case 2 × 487.5 = 975) and 3× on a phone (3 × 191 = 573).
 * Above 1024 the five-column desktop grid takes over at 248 CSS px, so nothing
 * larger is ever selected.
 */
export const AREA_TILE_WIDTHS = [400, 600, 800, 1000] as const;

export function getAreaTileSrcSet(url: string | null | undefined): string {
  if (!url) return '';
  return AREA_TILE_WIDTHS
    .map(w => `${getOptimizedUrl(url, { width: w, quality: 75 })} ${w}w`)
    .join(', ');
}

/**
 * Legacy alias for backward compatibility
 * @deprecated Use getOptimizedUrl or getCardThumbnailUrl instead
 */
export function getPropertyImageUrl(path: string | null | undefined, width: number = 800): string {
  return getOptimizedUrl(path, { width, quality: 80 });
}

/**
 * Legacy alias for backward compatibility
 * @deprecated Use getPropertySrcSet instead
 */
export function getPropertyImageSrcSet(path: string | null | undefined, sizes: number[] = [400, 800, 1200]): string {
  if (!path) return '';
  return sizes
    .map(size => `${getOptimizedUrl(path, { width: size, quality: 80 })} ${size}w`)
    .join(', ');
}

/**
 * Default image dimensions for common use cases
 * Helps prevent layout shift (CLS)
 */
export const IMAGE_DIMENSIONS = {
  propertyCard: { width: 800, height: 533 },    // 3:2 aspect ratio
  propertyThumbnail: { width: 400, height: 267 },
  propertyHero: { width: 1200, height: 800 },
  avatar: { width: 64, height: 64 },
  avatarLarge: { width: 128, height: 128 },
  spotlightCard: { width: 800, height: 533 },
  listCard: { width: 400, height: 267 },
} as const;
